import { Component, Input as AngularInput, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { IonContent, IonIcon, NavController } from '@ionic/angular/standalone';


import { ButtonComponent } from 'src/app/components/elements/button/button.component';
import { InputComponent } from 'src/app/components/elements/input/input.component';
import { Field, Input, InputType, InputOptions } from 'src/app/interfaces/components/elements/Input';
import { FormService } from 'src/app/services/utils/form/form.service';
import { ApiResponse, ApiService } from 'src/app/services/api/api.service';
import { STATES } from 'src/app/constants/states';
import { distinctUntilChanged } from 'rxjs/internal/operators/distinctUntilChanged';
import { ToastStatus, UtilsService } from 'src/app/services/utils.service';
import { ModalService } from 'src/app/services/utils/modal/modal.service';

@Component({
  selector: 'app-franchisee-form',
  templateUrl: './franchisee-form.page.html',
  styleUrls: ['./franchisee-form.page.scss'],
  standalone: true,
  imports: [
    IonIcon,
    IonContent,
    CommonModule,
    FormsModule,
    ButtonComponent,
    ReactiveFormsModule,
    InputComponent
  ]
})
export class FranchiseeFormPage implements OnInit {


  public form: FormGroup;
  public fields: Input[] = [
    {
      type: InputType.Text,
      placeholder: 'Nome completo',
      formControlName: 'completeName',
      value: '',
      validators: [
        Validators.required,
        Validators.pattern(/^[^0-9!@#$%¨&*()_\-+{}\[\]<>,:;|\\\/?]+$/),
      ],
    },
    {
      type: InputType.Tel,
      placeholder: 'Telefone com DDD',
      formControlName: 'telephone',
      mask: '(00) 00000-0000',
      value: '',
      validators: [Validators.required, this.formService.validateTel()],
    },
    {
      type: InputType.Email,
      placeholder: 'E-mail',
      formControlName: 'mail',
      value: '',
      validators: [Validators.required, Validators.email],
    },
    {
      field: Field.Select,
      placeholder: 'UF',
      formControlName: 'state',
      value: '',
      inline: true,
      options: STATES,
      validators: [Validators.required],
    },
    {
      field: Field.Select,
      placeholder: 'Cidade de interesse',
      formControlName: 'city',
      value: '',
      inline: true,
      options: [
        {
          id: '1',
          label: 'Selecione o estado',
          value: '',
        },
      ],
      validators: [Validators.required],
    },
    {
      field: Field.Select,
      placeholder: 'Investimento',
      formControlName: 'investment',
      value: '',
      options: [
        {
          id: '1',
          label: 'Até R$ 200 mil',
          value: 'Até R$ 200 mil',
        },
        {
          id: '2',
          label: 'R$ 200 mil a R$ 300 mil',
          value: 'R$ 200 mil a R$ 300 mil',
        },
        {
          id: '3',
          label: 'R$ 300 mil a R$ 400 mil',
          value: 'R$ 300 mil a R$ 400 mil',
        },
        {
          id: '4',
          label: 'R$ 400 mil a R$ 500 mil',
          value: 'R$ 400 mil a R$ 500 mil',
        },
        {
          id: '5',
          label: 'R$ 500 mil a R$ 1 milhão',
          value: 'R$ 500 mil a R$ 1 milhão',
        },
        {
          id: '6',
          label: 'Acima de R$ 1 milhão',
          value: 'Acima de R$ 1 milhão',
        },
        {
          id: '7',
          label: 'Não quero informar',
          value: 'Não quero informar',
        },
      ],
      validators: [Validators.required],
    },
    {
      field: Field.Select,
      placeholder: 'Franquia de interesse',
      formControlName: 'franchisee',
      value: '',
      options: [
        {
          id: '1',
          label: 'China in Box',
          value: 'China in Box',
        },
        {
          id: '2',
          label: 'Gendai',
          value: 'Gendai',
        },
        {
          id: '3',
          label: 'Koni',
          value: 'Koni',
        },
        {
          id: '4',
          label: 'Asa Açaí',
          value: 'Asa Açaí',
        },
        {
          id: '5',
          label: 'Spoleto',
          value: 'Spoleto',
        },
        {
          id: '6',
          label: 'Casa do Pão de Queijo',
          value: 'Casa do Pão de Queijo',
        },
        {
          id: '7',
          label: 'Desejo ser multifranqueado',
          value: 'Multifranqueado',
        },
      ],
      validators: [Validators.required],
    },
    {
      field: Field.Checkbox,
      formControlName: 'notification',
      value: false,
      label: 'Aceito receber notificações por SMS para promoções e vantagens.',
    },
  ];

  constructor(
    private formBuilder: FormBuilder,
    private formService: FormService,
    private apiService: ApiService,
    private utilsService: UtilsService,
    public modalService: ModalService,
    private navController: NavController,
  ) {
    this.form = this.formBuilder.group({});
  }

  ngOnInit() {
    this.setForm();
    setTimeout(() => {
      const element = document.querySelector('.progress-bar') as HTMLElement;
      if (element) {
        element.classList.add('fixed-on-top');
      }
    }, 2000);
  }

  private setForm(): void {
    this.fields.forEach((field) => {
      if (field.formControlName)
        this.form.addControl(
          field.formControlName,
          this.formBuilder.control(field.value, field.validators)
        );
    });

    this.form
      .get('state')
      ?.valueChanges.pipe(distinctUntilChanged())
      .subscribe((state) => {
        if (state) {
          this.apiService
            .get('cidades', { uf: state.toLowerCase() })
            .subscribe({
              next: (response: ApiResponse) => {
                if (response && response.data) {
                  const data: InputOptions[] = response.data;

                  const cities = data.map((city) => ({
                    id: city.id,
                    label: city.label,
                    value: city.value,
                  }));

                  const citySelect = this.fields.find(
                    (field) => field.formControlName === 'city'
                  );
                  if (citySelect) {
                    citySelect.options = cities;
                  }
                }
              },
              error: (err) => {
                console.error(err);
              },
            });
        }
      });
  }

  public validate(): void {
    if (
      this.formService.validateForm(this.form, {
        completeName: 'nome completo',
        telephone: 'telefone',
        mail: 'e-mail',
        state: 'estado',
        city: 'cidade de interesse',
        investment: 'investimento',
        franchisee: 'franquia de interesse',
      })
    ) {
      this.utilsService.trackFranchiseeFormStep('continuar', {
        cta_text: 'Continuar',
        cta_location: 'franchisee_modal_form',
        state: this.form.value.state || '',
        city: this.form.value.city || '',
        investment: this.form.value.investment || '',
        interestFranchise: this.form.value.franchisee || '',
      });
      this.navigate('/seja-franqueado');
    }
  }

  private submit(): void {
    const data = {
      nome_completo: this.form.value.completeName,
      telefone: this.form.value.telephone,
      email: this.form.value.mail,
      estado: this.form.value.state,
      cidade_interesse: this.form.value.city,
      investimento: this.form.value.investment,
      franquia_interesse: this.form.value.franchisee,
      notificacoes_sms: this.form.value.notification,
    };

    this.apiService.post('sejafranqueado', data).subscribe({
      next: (response: ApiResponse) => {
        if (response.message)
          this.utilsService.toast(response.message, ToastStatus.Success);

        this.form.reset();

        this.fields.forEach((field) => {
          if (field.formControlName) {
            this.form.get(field.formControlName)?.setValue(field.value);
          }
        });

        this.formService.clearFormData();
        this.utilsService.removeStorage('franchisee_name');
      },
      error: (err) => {
        this.utilsService.toast(err.message, ToastStatus.Error);
      },
    });
  }
  public navigate(
    path: string,
  ): void {
    this.utilsService.setStorage('franchisee_name', this.form.value.franchisee);
    this.formService.setPart1(this.form.value);
    this.navController.navigateForward(path);
    this.modalService.dismiss()
  }

  public closeModal(): void {
    this.modalService.dismiss();
  }
}
